const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/**
 * @description Hata metnini HTML'e karşı güvenli hâle getirir.
 *
 * NEDEN: Hata mesajları `InfoView.js` ve `showMessage.js` içinde `dangerouslySetInnerHTML` ile
 * basılıyor (locale metinleri `<br/>` içerdiği için innerHTML gerekiyor). Locale metinleri bizim
 * kontrolümüzde, ancak BE gövdesinden gelen metin kullanıcı girdisi taşıyabilir — o metni işaretleme
 * olarak yorumlatmamak için kaçırıyoruz.
 *
 * @param {string} value - Kaçırılacak metin.
 * @returns {string} HTML'e güvenli metin.
 */
const escapeHtml = (value) => value.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);

/**
 * @description API hata gövdesinden kullanıcıya gösterilebilecek metni çıkarır.
 *
 * NEDEN: BE iş kurallarını API-Platform (hydra) hata gövdesiyle bildiriyor — ör. medya teklifi
 * kararı verilmeden aşama onaylanmak istendiğinde `400` + `hydra:description: "Tüm medya
 * tekliflerine karar verilmeden bu aşama onaylanamaz."`. Çağrı yerleri bu gövdeyi okumayıp yalnız
 * `error.status`'ü generic bir metne bastığında, kullanıcı "Hata Kodu: 400" görüp neyi düzelteceğini
 * bilemiyordu. Bu util, iş kuralı mesajını tek yerden çıkarır.
 *
 * Desteklenen gövde şekilleri (öncelik sırasıyla):
 * 1. `hydra:description` — API-Platform iş kuralı / doğrulama mesajı (asıl kaynak).
 * 2. `violations[0].message` — alan bazlı doğrulama hatası (`422`).
 * 3. `detail` / `message` — hydra dışı düz hata gövdesi.
 * 4. `error.error` — RTK Query'nin ağ/parse hatası (`FETCH_ERROR`, `PARSING_ERROR`) metni.
 *
 * Hata objesi hem RTK Query (`{ status, data }`) hem axios interceptor'ı (`customAxios.js` zaten
 * description'ı çıkarıp düz string reject eder) şekillerini karşılar; bu yüzden `error`'ın kendisi
 * string ise doğrudan o kullanılır.
 *
 * Dönen metin HTML'e karşı kaçırılmıştır (bkz. `escapeHtml`) — çağıran taraf ek işlem yapmaz.
 *
 * @param {unknown} error - RTK Query `error` objesi, axios reject değeri ya da düz string.
 * @returns {string|null} Gösterilebilir hata metni; gövdede anlamlı bir mesaj yoksa `null`.
 */
export const getApiErrorMessage = (error) => {
  if (!error) return null;

  // customAxios.js:92 hata gövdesini zaten `hydra:description` string'ine indiriyor.
  if (typeof error === 'string') return error.trim() ? escapeHtml(error) : null;

  const data = error.data ?? error;

  const message =
    data?.['hydra:description'] ??
    (Array.isArray(data?.violations) ? data.violations[0]?.message : undefined) ??
    data?.detail ??
    // `message`'ı yalnız gövdenin içinden okuruz; RTK Query'nin hata objesinde `message` alanı
    // YOKTUR (bu yüzden `error.message` okuyan çağrı yerleri sessizce `undefined` basıyordu).
    (typeof data?.message === 'string' ? data.message : undefined) ??
    // Ağ/parse hatalarında gövde yoktur; RTK Query metni `error.error` alanında taşır.
    (typeof error.error === 'string' ? error.error : undefined);

  if (typeof message !== 'string' || !message.trim()) return null;

  return escapeHtml(message);
};
